import { AsyncPipe } from '@angular/common';
import {
  AfterContentChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  ViewRef,
} from '@angular/core';
import { ActiveCartFacade } from '@spartacus/cart/base/root';
import {
  CmsBreadcrumbsComponent,
  CmsService,
  ContentPageMetaResolver,
  Page,
  PageMeta,
  PageMetaService,
  PageType,
  TranslationService,
} from '@spartacus/core';
import { CmsComponentData } from '@spartacus/storefront';
import { DeviceDetectorService } from 'ngx-device-detector';
import { BehaviorSubject, Observable, Subscription, Unsubscribable, combineLatest, of } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { ComponentService } from 'src/app/service/component.service';
import { ShimanoCountryService } from 'src/app/service/country/shimano-country.service';
import { StorageService } from 'src/app/service/storage/storage.service';
import { CurrentBusinessType } from 'src/app/utility/current-businesstype';
import { ASM_ENABLED_LOCAL_STORAGE_KEY } from '../asm/asm-constant';

@Component({
  selector: 'app-breadcrumbs',
  templateUrl: './breadcrumbs.component.html',
  styleUrls: ['./breadcrumbs.component.scss'],
  providers: [AsyncPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class BreadcrumbsComponent implements OnInit, OnDestroy, AfterContentChecked {
  title$: Observable<string>;
  crumbs$: Observable<any[]>;
  breadcrumbs$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  pageTitle: string;
  pageLabel: string;
  pageId: string;
  newLabel: string;
  template: string;
  typeCode: string;
  currentPageHandler: Unsubscribable;
  titleHandler: Unsubscribable;
  header = document.querySelector('header');
  globalMsg = document.querySelector('cx-global-message');
  asmEnabled = this.storageService.getLocalStorageItem(ASM_ENABLED_LOCAL_STORAGE_KEY);
  isSavingCart = false;
  isSavingCartSubscription$: Subscription;
  isOrderFormSubscription$: Subscription;
  plpBreadcrumbsSubscription$: Subscription;
  plpBreadcrumbs: any;
  savedCartsNode: string;
  myAccountNode: string;
  cartPageId = 'CartPageTemplate';
  activeCountry$: Observable<string> = this.countryService.getActive();
  getActiveCountry = this.pipeAsync.transform(this.activeCountry$);
  isActiveCountrySOH: string = this.getActiveCountry === 'au' ? 'au' : this.getActiveCountry === 'nz' ? 'nz' : '';
  businessType: string;

  constructor(
    public component: CmsComponentData<CmsBreadcrumbsComponent>,
    protected pageMetaService: PageMetaService,
    protected contentPageService: ContentPageMetaResolver,
    private translation: TranslationService,
    protected cms: CmsService,
    public deviceDetectorService: DeviceDetectorService,
    public componentService: ComponentService,
    private cdr: ChangeDetectorRef,
    private countryService: ShimanoCountryService,
    private activeCartService: ActiveCartFacade,
    private pipeAsync: AsyncPipe,
    public currentBusinesstype: CurrentBusinessType,
    private storageService: StorageService
  ) {}

  ngOnInit() {
    this.pageLoad();

    this.isSavingCartSubscription$ = this.componentService.isSavingCart$.subscribe((inProgress) => {
      this.isSavingCart = inProgress;
      this.cdr.detectChanges();
    });

    this.isOrderFormSubscription$ = this.componentService.orderFormProdCategories$.subscribe((data) => {
      const orderFromBC: any[] = [];
      data.forEach((item: any) => {
        orderFromBC.push({
          label: item.name,
          link: item.url,
        });
      });
      if (orderFromBC.length > 0) {
        this.translation
          .translate('common.home')
          .pipe(take(1))
          .subscribe((textHome) => {
            orderFromBC.unshift({ label: textHome, link: '/' });
            this.breadcrumbs$.next(orderFromBC);
            this.crumbs$ = this.breadcrumbs$;
            if (!(this.cdr as ViewRef).destroyed) {
              this.cdr.detectChanges();
            }
          });
      }
    });

    // FOR PLP BREADCRUMBS
    this.plpBreadcrumbsSubscription$ = this.componentService.plpBreadcrumbs$
      .pipe(
        switchMap((plpBreadcrumbs) =>
          combineLatest([
            of(plpBreadcrumbs),
            this.cms.getCurrentPage().pipe(
              filter(Boolean),
              map((currentPage: Page) => currentPage.type === PageType.CATEGORY_PAGE)
            ),
          ])
        )
      )
      .subscribe(([breadcrumbs, isCategoryPage]) => {
        if (isCategoryPage) {
          this.plpBreadcrumbs = [];
          breadcrumbs.forEach((item: any) => {
            if (item.facetCode === 'category' || item.facetCode === 'allCategories') {
              this.plpBreadcrumbs.push({
                label: item.facetValueName,
                link: `/c/${item.facetValueCode}`,
              });
            }
          });
          if (this.plpBreadcrumbs.length > 0) {
            this.translation
              .translate('common.home')
              .pipe(take(1))
              .subscribe((textHome) => {
                this.plpBreadcrumbs.unshift({ label: textHome, link: '/' });
                this.breadcrumbs$.next(this.plpBreadcrumbs);
                this.crumbs$ = this.breadcrumbs$;
                this.cdr.detectChanges();
              });
          } else {
            this.translation
              .translate('common.home')
              .pipe(take(1))
              .subscribe((textHome) => {
                this.plpBreadcrumbs.unshift({ label: textHome, link: '/' });
                this.breadcrumbs$.next(this.plpBreadcrumbs);
                this.crumbs$ = this.breadcrumbs$;
                this.cdr.detectChanges();
              });
          }
        }
      });
    this.businessType = this.currentBusinesstype.getBusinessType();
  }

  pageLoad = () => {
    this.currentPageHandler = this.cms.getCurrentPage().subscribe((str) => {
      if (typeof str !== 'undefined') {
        this.pageLabel = str.label;
        this.pageId = str.pageId;
        this.template = str.template;
        this.typeCode = str.type;
        if (!this.pageId.includes('login')) {
          if (this.storageService.getLocalStorageItem('fromLoggedIn') === 'true') {
            this.storageService.removeLocalStorageItem('fromLoggedIn');
            this.header.style.display = 'block';
          }
          this.globalMsg.setAttribute('class', 'showCxGlobalMessage');
        }
        if (this.pageId.includes('login')) {
          this.globalMsg.removeAttribute('class');
        }
        if (this.pageId.includes('search')) {
          this.combineLatest();
          return;
        }
        if (str.type.toLowerCase() === PageType.CONTENT_PAGE.toLowerCase()) {
          this.setTitle();
          this.titleHandler = this.title$.subscribe((title) => {
            this.pageTitle = title;
          });
          if (this.template !== 'ShimanoOrderFormPageTemplate') {
            this.setBreadCrumbs();
          }

          if (this.template !== 'OrderConfirmationPageTemplate') {
            this.globalMsg.classList.remove('inactive');
          }
        } else {
          if (this.template !== 'ProductListPageTemplate') {
            this.combineLatest();
          }
        }
      }
      this.cdr.detectChanges();
    });
  };

  private setTitle(): void {
    this.title$ = this.pageMetaService.getMeta().pipe(
      filter(Boolean),
      map((meta: PageMeta) => meta.heading || meta.title)
    );
  }
  private setBreadCrumbs(): void {
    this.combineLatest();
    this.crumbs$.pipe(take(1)).subscribe((next) => {
      if (typeof this.pageTitle !== 'undefined' && typeof this.pageLabel !== 'undefined') {
        const linkArray: any = this.pageLabel.split('/');
        const count = linkArray.length;
        if (count >= 3) {
          for (const key in linkArray) {
            if (linkArray[key] !== '' && Number(key) < count - 1) {
              if (linkArray[key] === 'my-account' || linkArray[key] === 'saved-cart') {
                if (linkArray[key] === 'saved-cart') {
                  this.translation
                    .translate('cart.savedCartList.savedCartHeader')
                    .pipe(take(1))
                    .subscribe((savedCartsNode) => (this.savedCartsNode = savedCartsNode));
                  const link = '/' + linkArray[key];
                  this.removeDuplicateBreadcrumbs(next, this.savedCartsNode, link);
                } else if (linkArray[key] === 'my-account') {
                  this.translation
                    .translate('myAccount.title')
                    .pipe(take(1))
                    .subscribe((myAccountNode) => (this.myAccountNode = myAccountNode));
                  const link = '/' + linkArray[key];
                  this.removeDuplicateBreadcrumbs(next, this.myAccountNode, link);
                } else {
                  const label = linkArray[key].replace('-', ' ');
                  const link = '/' + linkArray[key];
                  this.removeDuplicateBreadcrumbs(next, label, link);
                }
              } else {
                const label = linkArray[key];
                const link = '/' + linkArray[key];
                this.removeDuplicateBreadcrumbs(next, label, link);
              }
            }
          }
        }
        if (this.pageId.includes('Checkout')) {
          for (const key in linkArray) {
            if (linkArray[key] !== '') {
              if (key === '1') {
                this.translation
                  .translate('cart.cartDetails')
                  .pipe(take(1))
                  .subscribe((labelText) => (this.newLabel = labelText));
                this.removeDuplicateBreadcrumbs(next, this.newLabel, '/cart');
                this.removeDuplicateBreadcrumbs(next, this.pageTitle, this.pageLabel);
              }
            }
          }
        } else {
          this.removeDuplicateBreadcrumbs(next, this.pageTitle, this.pageLabel);
        }
      }
      this.breadcrumbs$.next(next);
      this.crumbs$ = this.breadcrumbs$;
      this.cdr.detectChanges();
    });
  }

  combineLatest = () => {
    this.crumbs$ = combineLatest([this.pageMetaService.getMeta(), this.translation.translate('common.home')]).pipe(
      map(([meta, textHome]) => (meta && meta.breadcrumbs ? meta.breadcrumbs : [{ label: textHome, link: '/' }]))
    );
    this.cdr.detectChanges();
  };

  get isEmptyCart$(): Observable<boolean> {
    return this.activeCartService.getActive().pipe(map((activeCart) => activeCart.totalItems === 0));
  }
  get isCartPage$(): Observable<boolean> {
    return this.cms
      .getCurrentPage()
      .pipe(map((currentPage) => currentPage && currentPage.label && currentPage.label.includes('/cart')));
  }

  isMobileEmptyCartPage(isMobile: any, emptyCartPage: any): boolean {
    if (this.businessType === 'fish' && isMobile && emptyCartPage) {
      return true;
    } else {
      return false;
    }
  }

  removeDuplicateBreadcrumbs(breadcrumbs: any, label: any, link: any): any {
    // if condition is to prevent duplicate breadcrumbs
    if (breadcrumbs.filter((data: any) => data.link === this.pageLabel).length <= 0) {
      breadcrumbs.push({
        label: label,
        link: link,
      });
    }
  }

  ngAfterContentChecked() {
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    if (this.currentPageHandler) {
      this.currentPageHandler.unsubscribe();
    }
    if (this.titleHandler) {
      this.titleHandler.unsubscribe();
    }
    if (this.isSavingCartSubscription$) {
      this.isSavingCartSubscription$.unsubscribe();
    }
    if (this.plpBreadcrumbsSubscription$) {
      this.plpBreadcrumbsSubscription$.unsubscribe();
    }
  }
}
